<template>
  <section class="ci">
    <!-- HERO -->
    <div v-if="state === 'hero'" class="ci__hero" :style="heroBgStyle">
      <div class="ci__hero-grid">
        <div class="ci__hero-left">
          <div class="ci__hero-label">CUSTOM INQUIRY</div>
        </div>

        <div class="ci__hero-mid">
          <p class="ci__hero-copy">
            J. Hannah creates fine jewelry & objects meant to last and treasure; for living and living
            with. Our collections reinterpret the sustainability and simplicity of heirloom pieces with
            clean lines and modern details.
          </p>
        </div>

        <div class="ci__hero-right">
          <button class="ci__hero-cta" type="button" @click="enterForm">START A CUSTOM INQUIRY</button>
        </div>
      </div>
    </div>

    <!-- FORM -->
    <div v-else class="ci__form">
      <div class="ci__form-top">
        <div class="ci__form-title">CUSTOM INQUIRY FORM</div>
        <button class="ci__reset" type="button" @click="resetAll">RESET</button>
      </div>

      <!-- SUBMITTED -->
      <div v-if="state === 'submitted'" class="ci__submitted">
        <div class="ci__submitted-inner">
          <div class="ci__submitted-title">SUBMITTED</div>
          <p class="ci__submitted-copy">
            Thanks for your submission, our team will be in contact with the next steps to bring your
            project to life.
          </p>
        </div>
      </div>

      <!-- STEPS -->
      <div v-else class="ci__form-grid">
        <!-- LEFT NAV -->
        <aside class="ci__left">
          <div class="ci__steps">
            <div
              v-for="s in steps"
              :key="s.key"
              class="ci__step"
              :class="{
                'is-active': s.index === currentStep,
                'is-complete': s.index < currentStep,
              }"
            >
              <div class="ci__step-roman">{{ s.roman }}</div>

              <div class="ci__step-body">
                <div class="ci__step-title">{{ s.title }}</div>
              </div>

              <!-- Only show subtitle under the ACTIVE step -->
              <div v-if="s.index === currentStep" class="ci__step-subtitle">
                {{ s.subtitle }}
              </div>
            </div>
          </div>
        </aside>

        <!-- RIGHT STACKED PANELS -->
        <main class="ci__right">
          <div class="ci__panel-stack">
            <!-- STEP 1 -->
            <section class="ci__panel-step" :class="stepClasses(1)">
              <header class="ci__panel-header">
                <div class="ci__panel-num">01</div>
                <div class="ci__panel-title">CONTACT</div>
                <div class="ci__panel-subtitle">{{ steps[0].subtitle }}</div>
              </header>

              <div class="ci__panel-body">
                <div class="ci__fields">
                  <!-- CHANGED: combined name field -->
                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.name"
                      type="text"
                      autocomplete="name"
                      placeholder=" "
                      :disabled="currentStep !== 1"
                      @blur="touch('name')"
                    />
                    <span class="ci__label">Name</span>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.email"
                      type="email"
                      autocomplete="email"
                      placeholder=" "
                      :disabled="currentStep !== 1"
                      @blur="touch('email')"
                    />
                    <span class="ci__label">Email Address</span>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.phone"
                      type="tel"
                      autocomplete="tel"
                      placeholder=" "
                      :disabled="currentStep !== 1"
                      @blur="touch('phone')"
                    />
                    <span class="ci__label">Phone No.</span>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.country"
                      type="text"
                      autocomplete="country-name"
                      placeholder=" "
                      :disabled="currentStep !== 1"
                      @blur="touch('country')"
                    />
                    <span class="ci__label">Country</span>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.stateCity"
                      type="text"
                      autocomplete="address-level1"
                      placeholder=" "
                      :disabled="currentStep !== 1"
                      @blur="touch('stateCity')"
                    />
                    <span class="ci__label">State / City</span>
                  </label>
                </div>

                <button
                  class="ci__next"
                  type="button"
                  @click="nextFrom(1)"
                  :disabled="currentStep !== 1"
                >
                  NEXT
                </button>

                <p v-if="stepError && currentStep === 1" class="ci__step-error">
                  {{ stepError }}
                </p>
              </div>
            </section>

            <!-- STEP 2 -->
            <section class="ci__panel-step" :class="stepClasses(2)">
              <header class="ci__panel-header">
                <div class="ci__panel-num">02</div>
                <div class="ci__panel-title">PROJECT</div>
                <div class="ci__panel-subtitle">{{ steps[1].subtitle }}</div>
              </header>

              <div class="ci__panel-body">
                <div class="ci__fields">
                  <label class="ci__field">
                    <select
                      class="ci__input"
                      v-model="form.projectType"
                      :disabled="currentStep !== 2"
                      @blur="touch('projectType')"
                    >
                      <option value="" disabled>Project Type</option>
                      <option v-for="opt in PROJECT_TYPE_OPTIONS" :key="opt" :value="opt">
                        {{ opt }}
                      </option>
                    </select>
                    <span class="ci__label">Project Type</span>
                  </label>

                  <label class="ci__field">
                    <textarea
                      class="ci__input ci__textarea"
                      v-model="form.projectDescription"
                      rows="5"
                      placeholder=" "
                      :disabled="currentStep !== 2"
                      @blur="touch('projectDescription')"
                    />
                    <span class="ci__label">Message</span>
                  </label>

                  <label class="ci__field ci__file">
                    <input
                      class="ci__file-input"
                      type="file"
                      multiple
                      accept="image/*"
                      :disabled="currentStep !== 2"
                      @change="onFilesChange"
                    />

                    <div class="ci__file-ui">
                      <span class="ci__file-placeholder">Reference Images</span>
                      <span class="ci__file-action">Upload</span>
                    </div>

                    <div v-if="files.length" class="ci__filelist">
                      <div v-for="f in files" :key="f.name + f.size" class="ci__fileitem">
                        {{ f.name }}
                      </div>
                    </div>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.piecesDrawnTo"
                      type="text"
                      placeholder=" "
                      :disabled="currentStep !== 2"
                      @blur="touch('piecesDrawnTo')"
                    />
                    <span class="ci__label">J.Hannah pieces you’re drawn to…</span>
                  </label>
                </div>

                <button
                  class="ci__next"
                  type="button"
                  @click="nextFrom(2)"
                  :disabled="currentStep !== 2"
                >
                  NEXT
                </button>

                <p v-if="stepError && currentStep === 2" class="ci__step-error">
                  {{ stepError }}
                </p>
              </div>
            </section>

            <!-- STEP 3 -->
            <section class="ci__panel-step" :class="stepClasses(3)">
              <header class="ci__panel-header">
                <div class="ci__panel-num">03</div>
                <div class="ci__panel-title">DETAILS</div>
                <div class="ci__panel-subtitle">{{ steps[2].subtitle }}</div>
              </header>

              <div class="ci__panel-body">
                <div class="ci__fields">
                  <label class="ci__field">
                    <select
                      class="ci__input"
                      v-model="form.budget"
                      :disabled="currentStep !== 3"
                      @blur="touch('budget')"
                    >
                      <option value="" disabled>Budget</option>
                      <option v-for="opt in BUDGET_OPTIONS" :key="opt" :value="opt">
                        {{ opt }}
                      </option>
                    </select>
                    <span class="ci__label">Budget</span>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.timeline"
                      type="text"
                      placeholder=" "
                      :disabled="currentStep !== 3"
                      @blur="touch('timeline')"
                    />
                    <span class="ci__label">Timeline</span>
                  </label>

                  <label class="ci__field">
                    <input
                      class="ci__input"
                      v-model="form.howHear"
                      type="text"
                      placeholder=" "
                      :disabled="currentStep !== 3"
                      @blur="touch('howHear')"
                    />
                    <span class="ci__label">How did you hear about J.Hannah?</span>
                  </label>
                </div>

                <button
                  class="ci__next"
                  type="button"
                  @click="nextFrom(3)"
                  :disabled="currentStep !== 3"
                >
                  NEXT
                </button>

                <p v-if="stepError && currentStep === 3" class="ci__step-error">
                  {{ stepError }}
                </p>
              </div>
            </section>

            <!-- STEP 4 -->
            <section class="ci__panel-step" :class="stepClasses(4)">
              <header class="ci__panel-header">
                <div class="ci__panel-num">04</div>
                <div class="ci__panel-title">REVIEW AND SEND</div>
                <div class="ci__panel-subtitle">{{ steps[3].subtitle }}</div>
              </header>

              <div class="ci__panel-body">
                <div class="ci__review-intro"></div>
                <dl class="ci__review">
                  <!-- STEP 1 -->
                  <div class="ci__review-row">
                    <dt class="ci__review-label">NAME</dt>
                    <dd class="ci__review-value">{{ form.name }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">EMAIL ADDRESS</dt>
                    <dd class="ci__review-value">{{ form.email }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">PHONE NO.</dt>
                    <dd class="ci__review-value">{{ form.phone }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">COUNTRY</dt>
                    <dd class="ci__review-value">{{ form.country }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">STATE / CITY</dt>
                    <dd class="ci__review-value">{{ form.stateCity }}</dd>
                  </div>

                  <!-- STEP 2 -->
                  <div class="ci__review-row">
                    <dt class="ci__review-label">PROJECT TYPE</dt>
                    <dd class="ci__review-value">{{ form.projectType }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">MESSAGE</dt>
                    <dd class="ci__review-value">{{ form.projectDescription }}</dd>
                  </div>

                  <div v-if="files.length" class="ci__review-row">
                    <dt class="ci__review-label">REFERENCE IMAGES</dt>
                    <dd class="ci__review-value">
                      <!-- use the File list you already have -->
                      <div v-for="f in files" :key="`${f.name}-${f.size}`">{{ f.name }}</div>
                    </dd>
                  </div>

                  <div v-else class="ci__review-row">
                    <dt class="ci__review-label">REFERENCE IMAGES</dt>
                    <dd class="ci__review-value">None</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">J.HANNAH PIECES YOU’RE DRAWN TO</dt>
                    <dd class="ci__review-value">{{ form.piecesDrawnTo }}</dd>
                  </div>

                  <!-- STEP 3 -->
                  <div class="ci__review-row">
                    <dt class="ci__review-label">BUDGET</dt>
                    <dd class="ci__review-value">{{ form.budget }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">TIMELINE</dt>
                    <dd class="ci__review-value">{{ form.timeline }}</dd>
                  </div>

                  <div class="ci__review-row">
                    <dt class="ci__review-label">HOW DID YOU HEAR ABOUT J.HANNAH?</dt>
                    <dd class="ci__review-value">{{ form.howHear }}</dd>
                  </div>
                </dl>

                <button
                  class="ci__submit"
                  type="button"
                  @click="submit"
                  :disabled="currentStep !== 4 || isSubmitting"
                >
                  {{ isSubmitting ? 'SUBMITTING…' : 'SUBMIT' }}
                </button>

                <p v-if="submitError" class="ci__submit-error">{{ submitError }}</p>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue'

type StepKey = 'contact' | 'project' | 'details' | 'review'

type Step = {
  key: StepKey
  index: number
  roman: string
  num: string
  title: string
  subtitle: string
  placeholder: string
}

type FormState = {
  // CHANGED
  name: string
  email: string
  phone: string
  country: string
  stateCity: string

  projectType: string
  budget: string
  piecesDrawnTo: string
  projectDescription: string

  timeline: string
  howHear: string
}

const state = ref<'hero' | 'form' | 'submitted'>('hero')
const currentStep = ref<number>(1)
const stepError = ref('')

/* ------------------------------------------------------------
   HERO-ONLY GLOBAL STYLES TOGGLE (NEW)
   We avoid :has() so the styles reliably turn off after "Start Inquiry".
------------------------------------------------------------ */
function setHeroBodyClass(active: boolean) {
  document.body.classList.toggle('ci-hero-active', active)
}

function enterForm() {
  state.value = 'form'
  setHeroBodyClass(false)
}

/**
 * Editable via Theme Editor section setting.
 * Read from:
 * <page-template-custom-inquiry data-hero-image="..."></page-template-custom-inquiry>
 */
const heroImage = ref<string>('')

/**
 * Vercel API base (optional per environment).
 * Read from:
 * <page-template-custom-inquiry data-api-base="https://..."></page-template-custom-inquiry>
 */
const apiBase = ref<string>('')

function readApiBaseFromDom() {
  // Prefer: walk up from where the Vue app is actually mounted
  const rootEl = (getCurrentInstance()?.proxy?.$el as HTMLElement) || null
  const host = rootEl?.closest('page-template-custom-inquiry') as HTMLElement | null
  if (host?.dataset?.apiBase) return host.dataset.apiBase.trim()

  // Fallbacks (in case closest() fails for any reason)
  const byTag = document.querySelector('page-template-custom-inquiry') as HTMLElement | null
  if (byTag?.dataset?.apiBase) return byTag.dataset.apiBase.trim()

  const byAttr = document.querySelector('[data-api-base]') as HTMLElement | null
  return (byAttr?.dataset?.apiBase || '').trim()
}

onMounted(() => {
  setHeroBodyClass(true)

  const rootEl = (getCurrentInstance()?.proxy?.$el as HTMLElement) || null
  if (rootEl?.dataset?.heroImage) heroImage.value = rootEl.dataset.heroImage

  apiBase.value = readApiBaseFromDom()
})

const heroBgStyle = computed(() => {
  if (!heroImage.value) return undefined
  return { backgroundImage: `url(${heroImage.value})` }
})

const steps: Step[] = [
  {
    key: 'contact',
    index: 1,
    roman: 'I.',
    num: '01',
    title: 'Contact',
    subtitle: 'Please provide us with your contact information',
    placeholder: '',
  },
  {
    key: 'project',
    index: 2,
    roman: 'II.',
    num: '02',
    title: 'Project',
    subtitle: 'Let us know about your dreams',
    placeholder: '',
  },
  {
    key: 'details',
    index: 3,
    roman: 'III.',
    num: '03',
    title: 'Details',
    subtitle: 'Let us know about your dreams',
    placeholder: '',
  },
  {
    key: 'review',
    index: 4,
    roman: 'IV.',
    num: '04',
    title: 'Review',
    subtitle: 'Once reviewed and sent our team will be in touch',
    placeholder: '',
  },
]

const activeStep = computed(() => steps.find((s) => s.index === currentStep.value) || steps[0])

const PROJECT_TYPE_OPTIONS = ['Modify an existing design', 'Fully custom'] as const
const BUDGET_OPTIONS = [
  '$4,000-$8,000',
  '$8,000-$15,000',
  '$15,000-$20,000',
  '$20,000-$30,000',
  '$30,000 plus',
] as const

const form = ref<FormState>({
  // CHANGED
  name: '',
  email: '',
  phone: '',
  country: '',
  stateCity: '',

  projectType: '',
  budget: '',
  piecesDrawnTo: '',
  projectDescription: '',

  timeline: '',
  howHear: '',
})

const touched = ref<Record<string, boolean>>({})
function touch(key: keyof FormState) {
  touched.value[key as string] = true
  stepError.value = ''
}

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

const step1Valid = computed(() => {
  const f = form.value
  return (
    // CHANGED
    f.name.trim().length > 0 &&
    isEmail(f.email) &&
    f.phone.trim().length > 0 &&
    f.country.trim().length > 0 &&
    f.stateCity.trim().length > 0
  )
})

const step2Valid = computed(() => {
  const f = form.value
  return (
    f.projectType.trim().length > 0 &&
    f.piecesDrawnTo.trim().length > 0 &&
    f.projectDescription.trim().length > 0
  )
})

const step3Valid = computed(() => {
  const f = form.value
  return f.budget.trim().length > 0 && f.timeline.trim().length > 0 && f.howHear.trim().length > 0
})

const canGoNext = computed(() => {
  if (currentStep.value === 1) return step1Valid.value
  if (currentStep.value === 2) return step2Valid.value
  if (currentStep.value === 3) return step3Valid.value
  return true
})

function goNext() {
  stepError.value = ''

  if (!canGoNext.value) {
    if (currentStep.value === 1) {
      if (!isEmail(form.value.email)) {
        stepError.value = 'Please enter a valid email address'
      } else {
        stepError.value = 'Please enter all fields'
      }
    } else {
      stepError.value = 'Please enter all fields'
    }

    return
  }

  if (currentStep.value < 4) currentStep.value += 1
}

function canGoNextFor(stepIndex: number) {
  if (stepIndex === 1) return step1Valid.value
  if (stepIndex === 2) return step2Valid.value
  if (stepIndex === 3) return step3Valid.value
  return true
}

function nextFrom(stepIndex: number) {
  // only allow the "next" button on the active step to advance
  if (currentStep.value !== stepIndex) return
  goNext()
}

function stepClasses(stepIndex: number) {
  return {
    'is-active': currentStep.value === stepIndex,
    'is-complete': currentStep.value > stepIndex,
    'is-future': currentStep.value < stepIndex,
  }
}

const files = ref<File[]>([])
function onFilesChange(e: Event) {
  const input = e.target as HTMLInputElement
  const list = input.files ? Array.from(input.files) : []
  files.value = list
}

// CHANGED
const fullName = computed(() => form.value.name.trim())

const locationLine = computed(() => {
  const parts = [form.value.stateCity, form.value.country].map((s) => s.trim()).filter(Boolean)
  return parts.join(', ')
})

const isSubmitting = ref(false)
const submitError = ref('')

function endpoint(path: string) {
  const base = apiBase.value?.trim()
  if (!base) return path
  return `${base.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`
}

async function uploadReferenceImages(): Promise<string[]> {
  if (!files.value.length) return []

  const fd = new FormData()
  files.value.forEach((f) => fd.append('files', f, f.name))

  const res = await fetch(endpoint('/api/uploads'), {
    method: 'POST',
    body: fd,
  })

  const json = await res.json().catch(() => null)

  if (!res.ok) {
    console.error('Upload failed:', res.status, json)
    throw new Error(json?.error || 'Upload failed')
  }

  return Array.isArray(json?.urls) ? json.urls : []
}

async function submit() {
  submitError.value = ''

  if (!step1Valid.value || !step2Valid.value || !step3Valid.value) {
    submitError.value = 'Please complete all steps before submitting.'
    return
  }

  if (!apiBase.value) apiBase.value = readApiBaseFromDom()

  if (!apiBase.value) {
    submitError.value = 'Missing API base URL.'
    return
  }

  try {
    isSubmitting.value = true

    const uploadedUrls = await uploadReferenceImages()

    const payload = {
      test: true,
      recipientOverride: 'elliechappelledesign@gmail.com',

      // CHANGED
      name: form.value.name,
      email: form.value.email,
      phone: form.value.phone,
      country: form.value.country,
      stateCity: form.value.stateCity,

      projectType: form.value.projectType,
      budget: form.value.budget,
      piecesDrawnTo: form.value.piecesDrawnTo,
      projectDescription: form.value.projectDescription,

      timeline: form.value.timeline,
      howHear: form.value.howHear,

      referenceImages: uploadedUrls,
      referenceImageNames: files.value.map((f) => f.name),
    }

    const res = await fetch(endpoint('/api/custom-inquiry'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    const json = await res.json().catch(() => null)
    if (!res.ok) {
      console.error('Custom inquiry failed:', res.status, json)
      throw new Error(json?.error || 'Request failed')
    }

    console.log('Vercel response:', json)
    state.value = 'submitted'
    setHeroBodyClass(false)

    // scroll to top so the success message is visible
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  } catch (e: any) {
    console.error(e)
    submitError.value = e?.message || 'Submission failed'
  } finally {
    isSubmitting.value = false
  }
}

function resetAll() {
  currentStep.value = 1
  state.value = 'form'
  setHeroBodyClass(false)

  form.value = {
    // CHANGED
    name: '',
    email: '',
    phone: '',
    country: '',
    stateCity: '',
    projectType: '',
    budget: '',
    piecesDrawnTo: '',
    projectDescription: '',
    timeline: '',
    howHear: '',
  }

  touched.value = {}
  files.value = []
}
</script>

<style lang="scss">
@import '~styles/base.scss';

.ci input,
.ci textarea,
.ci select {
  font: inherit;
}

.ci__input:has(option[value='']:checked) {
  opacity: 0.6;
}

.ci__input {
  width: 100%;
  box-sizing: border-box;
}

select.ci__input {
  padding-left: 8px; // match your input padding
  padding-right: 28px; // room for arrow
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23000' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

/* Textarea label should sit at the top, not vertically centered */
.ci__textarea + .ci__label {
  top: 10px; // aligns with textarea padding
  transform: none;
}

/* ------------------------------------------------------------------
   HERO-ONLY GLOBAL OVERRIDES
   Controlled by JS class so they turn OFF after "Start Inquiry"
------------------------------------------------------------------- */
body.ci-hero-active {
  .header-desktop .shopify-link-dropdown__links .shopify-link:after {
    background: var(--color-light);
  }

  .layout-container {
    padding-top: 0 !important;
  }

  header,
  header a,
  header button,
  header span {
    color: var(--color-light) !important;
  }

  header svg {
    fill: var(--color-light) !important;
    stroke: var(--color-light) !important;
  }

  .header-logo,
  .header-logo svg,
  .header-logo img {
    filter: brightness(0) invert(1);
  }
}

/* ------------------------------------------------------------------
   COMPONENT STYLES
------------------------------------------------------------------- */

.ci {
  min-height: 100vh;
  max-width: 1200px;
  width: 70%;

  @include breakpoint('l') {
    width: 100%;
  }

  &__hero,
  &__form {
    min-height: 100vh;
  }

  &__left {
    position: sticky;
    top: 40px;
    align-self: flex-start;
  }

  /* =========================
     HERO (FULL BLEED)
  ========================= */
  &__hero {
    position: relative;
    width: 100vw;
    min-height: 100vh;

    /* break out of any constrained container */
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);

    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;

    display: flex;
    align-items: flex-start;
  }

  /* dark overlay */
  &__hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.28);
    z-index: 1;
  }

  &__hero-grid {
    position: relative;
    z-index: 2;

    display: grid;
    grid-template-columns: max-content minmax(0, 325px) 1fr;
    align-items: flex-start;

    width: 100%;
    padding: 16px;

    margin-top: 400px;
  }

  &__hero-left {
    @include type-xs-uppercase;
    color: #ffffff;
    margin-right: 40px;
  }

  &__hero-copy {
    @include type-xs;
    color: #ffffff;
  }

  &__hero-right {
    display: flex;
    justify-content: flex-end;
  }

  &__hero-cta {
    background: #ffffff;
    color: #000000;
    border: none;
    padding: 4px 48px 2px 48px;
    @include type-xxs-uppercase;
    cursor: pointer;
  }

  &__hero-label {
    width: 50%;
  }

  @include breakpoint('l') {
    &__hero-grid {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &__hero-right {
      width: 100%;
      display: flex;
    }

    &__hero-cta {
      width: 100%;
    }

    &__hero-label {
      width: 100%;
    }
  }

  /* =========================
     FORM LAYOUT
  ========================= */

  &__reset {
    @include type-xxs-uppercase;
    color: var(--color-midgrey);
    align-self: flex-end;
    margin-bottom: 4px;
  }

  &__form {
    padding: 0px 80px;
    margin-top: 190px;
  }

  &__form-title {
    margin-bottom: 16px;
    @include type-xxs-uppercase;
  }

  &__form-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    border-bottom: 1px solid var(--color-lightgrey);
  }

  &__form-grid {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 120px;
  }

  &__left {
    max-width: 150px;
  }

  @include breakpoint('l') {
    &__left {
      display: none;
    }
    &__form {
      padding: 0px 20px;
      margin-top: 0;
    }

    &__form-grid {
      grid-template-columns: 1fr;
    }
  }

  /* =========================
     STEPS / NAV
  ========================= */

  &__steps {
    display: grid;
    gap: 4px;
  }

  &__step {
    display: grid;
    grid-template-columns: 30px 1fr;
    gap: 4px;
    align-items: start;
    opacity: 0.5;

    &.is-active {
      opacity: 1;
    }
  }

  &__step-body {
    display: grid;
    gap: 6px;
  }

  &__step-title {
    @include type-xxs-uppercase;
  }

  &__step-subtitle {
    grid-column: 1 / -1;
    width: 100%;
    @include type-xs;
    display: block;
    margin-bottom: 16px;
  }

  /* =========================
     PANEL
  ========================= */

  &__panel-stack {
    display: grid;
    gap: 70px; // tweak to match comp spacing between sections
  }

  &__panel-step {
    opacity: 0.5; // default dim
    transition: opacity 200ms ease;
  }

  &__panel-step.is-active {
    opacity: 1;
  }

  // keep complete steps readable but still dimmed (optional)
  &__panel-step.is-complete {
    opacity: 0.5;
  }

  // prevent interacting with non-active inputs, but keep them visible
  &__panel-step:not(.is-active) {
    pointer-events: none;
  }

  // allow clicks on the active step only
  &__panel-step.is-active {
    pointer-events: auto;
  }

  // make disabled inputs still look like the comp (not “greyed out”)
  &__input:disabled {
    opacity: 1;
    -webkit-text-fill-color: currentColor;
  }

  // NEXT button in dimmed steps should still appear, just not clickable
  &__panel-step:not(.is-active) .ci__next {
    opacity: 0.5;
  }

  &__panel {
    padding-top: 16px;
    margin-bottom: 40px;
  }

  &__panel-header {
    display: grid;
    grid-template-columns: 60px 1fr;
    column-gap: 12px;
    align-items: baseline;
    margin-bottom: 32px;
    @include type-xs;
  }

  &__panel-num {
    grid-column: 1;
    grid-row: 1;
    margin-bottom: 4px;
  }

  &__panel-title {
    grid-column: 2;
    grid-row: 1;
    @include type-xxs-uppercase;
    margin-bottom: 4px;
  }

  /* subtitle sits below the rule, usually aligned with the title column */
  &__panel-subtitle {
    grid-column: 1 / -1; /* span both columns */
    grid-row: 2;
    border-top: 1px solid var(--color-lightgrey);
    padding-top: 8px;
  }

  /* =========================
     FIELDS
  ========================= */
  &__fields {
    display: grid;
    gap: 8px;
    margin-top: 18px;
  }

  &__field {
    display: grid;
    position: relative;
  }

  &__label {
    @include type-xs;
    color: var(--color-midgrey);

    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;

    transition: color 140ms ease;
  }

  &__input {
    padding: 8px 8px;
    background: var(--color-offwhite);
    outline: none;
    border: none;
    width: 100%;
    box-sizing: border-box;
    @include type-xs;
  }

  &__textarea {
    resize: vertical;
    min-height: 80px;
    padding: 8px 8x;
    @include type-xs;
  }

  &__textarea {
    font: inherit;
  }

  &__input:focus + .ci__label {
    opacity: 0;
  }

  &__input:not(:placeholder-shown) + .ci__label {
    opacity: 0;
  }

  &__help {
    @include type-xs;
    color: var(--color-midgrey);
  }

  /* File upload styled like a text field with "Upload" on the right */
  &__file {
    position: relative;
  }

  /* The real input sits on top, invisible, so clicking anywhere opens picker */
  &__file-input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }

  /* The visible “fake input” row */
  &__file-ui {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 8px;
    background: var(--color-offwhite);
    box-sizing: border-box;
  }

  /* Left placeholder text */
  &__file-placeholder {
    @include type-xs;
    color: var(--color-midgrey);
  }

  /* Right action text */
  &__file-action {
    @include type-xs;
    color: var(--color-midgrey);
  }

  &__fileitem {
    padding-top: 2px;
  }

  /* =========================
     BUTTONS
  ========================= */

  /* put this near your buttons / errors section */

  &__step-error {
    margin-top: 10px;
    @include type-xs;
  }

  &__next {
    margin-top: 18px;
    padding: 4px 12px 2px 12px;
    @include type-xxs-uppercase;
    background: var(--color-dark);
    color: var(--color-light);
  }

  &__submit {
    margin-top: 18px;
    padding: 4px 12px 2px 12px;
    @include type-xxs-uppercase;
    width: 100%;
    background: var(--color-dark);
    color: var(--color-light);
    text-align: center;
    box-sizing: border-box;
  }

  &__submit-error {
    margin-top: 10px;
    @include type-xs;
  }

  /* =========================
     REVIEW
  ========================= */
  &__review-intro {
    margin-top: 6px;
    margin-bottom: 22px;
    color: var(--color-midgrey);
  }

  &__review {
    display: grid;
    gap: 16px;
    margin: 0;
  }

  &__review-row {
    display: grid;
    gap: 6px;
  }

  &__review-label {
    @include type-xxs-uppercase;
    color: var(--color-midgrey);
    margin: 0;
  }

  &__review-value {
    margin: 0;
  }

  /* =========================
     GHOST / PREVIEW
  ========================= */
  &__ghost-row {
    margin-top: 18px;
    opacity: 0.5;

    &.is-dim {
      opacity: 0.5;
    }
  }

  &__ghost-head {
    display: grid;
    grid-template-columns: 60px 1fr;
    gap: 12px;
    align-items: baseline;
  }

  &__ghost-line {
    border-top: 1px solid var(--color-lightgrey);
    margin-top: 10px;
  }

  /* =========================
     SUBMITTED
  ========================= */
  &__submitted {
    padding-top: 16px;
    width: 50%;
    float: right;
  }

  &__submitted-inner {
    max-width: 640px;
    margin-left: auto;
  }

  &__submitted-title {
    @include type-xxs-uppercase;
    margin-bottom: 4px;
    color: var(--color-midgrey);
    border-bottom: 1px solid var(--color-lightgrey);
  }

  &__submitted-copy {
    margin: 0;
    padding-top: 8px;
    max-width: 250px;
  }
}
</style>
